import { Outlet, Link, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useEffect, useState, useCallback } from 'react';
import { LayoutDashboard, Calendar, ShoppingBag, Users, BookOpen, ArrowLeft, CreditCard, Star, Clock, Music, Handshake } from 'lucide-react';
import api from '../lib/api';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_mocktail-hub-6/artifacts/yiexn6qe_Simple%20Modern%20Minimalist%20Circle%20Design%20Studio%20Logo.png";

export default function AdminLayout() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [counts, setCounts] = useState({ rdv: 0, reservations: 0, orders: 0 });

  const fetchCounts = useCallback(() => {
    if (!user || user.role !== 'admin') return;
    Promise.all([
      api.get('/admin/rdv').catch(() => ({ data: [] })),
      api.get('/admin/reservations').catch(() => ({ data: [] })),
      api.get('/admin/reward-orders').catch(() => ({ data: [] })),
    ]).then(([r, res, ord]) => {
      setCounts({
        rdv: (r.data || []).filter(x => x.status === 'pending').length,
        reservations: (res.data || []).filter(x => x.status === 'pending').length,
        orders: (ord.data || []).filter(x => x.status === 'pending').length,
      });
    });
  }, [user]);

  useEffect(() => { fetchCounts(); const i = setInterval(fetchCounts, 30000); return () => clearInterval(i); }, [fetchCounts]);

  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="spin-gold w-10 h-10" /></div>;
  if (!user || user.role !== 'admin') return <Navigate to="/login" replace />;

  const sidebarItems = [
    { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true, badge: 0 },
    { to: '/admin/events', label: 'Événements', icon: Calendar, badge: 0 },
    { to: '/admin/products', label: 'Catalogue', icon: ShoppingBag, badge: 0 },
    { to: '/admin/rdv', label: 'RDV', icon: Clock, badge: counts.rdv },
    { to: '/admin/sumup', label: 'SumUp', icon: CreditCard, badge: 0 },
    { to: '/admin/clients', label: 'Clients', icon: Users, badge: 0 },
    { to: '/admin/loyalty', label: 'Fidélité', icon: Star, badge: counts.orders },
    { to: '/admin/reservations', label: 'Demandes', icon: BookOpen, badge: counts.reservations },
    { to: '/admin/partners', label: 'Partenaires', icon: Handshake, badge: 0 },
  ];

  const totalBadge = counts.rdv + counts.reservations + counts.orders;

  return (
    <div className="min-h-screen bg-[#050505] text-white flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col w-60 bg-[#0A0A0A] border-r border-white/5 shrink-0">
        <div className="p-4 flex items-center gap-3 border-b border-white/5">
          <img src={LOGO_URL} alt="RSX" className="h-8 w-8 rounded-full" />
          <div className="min-w-0">
            <div className="text-sm font-semibold" style={{ fontFamily: 'Outfit' }}>RSX Admin</div>
            <div className="text-[10px] text-[#A1A1AA] truncate">{user.email}</div>
          </div>
          {totalBadge > 0 && <span className="ml-auto bg-[#EF4444] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center pulse-gold">{totalBadge}</span>}
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {sidebarItems.map(({ to, label, icon: Icon, exact, badge }) => {
            const active = exact ? location.pathname === to : location.pathname.startsWith(to) && to !== '/admin';
            return (
              <Link key={to} to={to} data-testid={`admin-nav-${label.toLowerCase().replace(/é/g,'e')}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all duration-200 ${
                  active ? 'bg-[#D4AF37]/15 text-[#D4AF37]' : 'text-[#A1A1AA] hover:text-white hover:bg-white/5'
                }`}>
                <Icon className="w-4 h-4 shrink-0" />
                <span className="truncate">{label}</span>
                {badge > 0 && <span className="ml-auto bg-[#EF4444] text-white text-[9px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center">{badge}</span>}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/5 space-y-1">
          <Link to="/admin/blindtest" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-red-400 hover:text-red-300 hover:bg-red-500/5" data-testid="admin-blindtest-link">
            <Music className="w-4 h-4" /> Blind Test / Quiz
          </Link>
          <Link to="/" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-[#A1A1AA] hover:text-white hover:bg-white/5" data-testid="admin-back-site">
            <ArrowLeft className="w-4 h-4" /> Retour au site
          </Link>
        </div>
      </aside>

      {/* Mobile/Tablet */}
      <div className="flex flex-col flex-1 min-w-0">
        <header className="lg:hidden glass-header flex items-center gap-3 px-4 h-14 border-b border-white/5 shrink-0">
          <img src={LOGO_URL} alt="RSX" className="h-7 w-7 rounded-full" />
          <span className="text-sm font-semibold" style={{ fontFamily: 'Outfit' }}>RSX Admin</span>
          {totalBadge > 0 && <span className="bg-[#EF4444] text-white text-[9px] font-bold w-5 h-5 rounded-full flex items-center justify-center">{totalBadge}</span>}
          <Link to="/" className="ml-auto text-xs text-[#A1A1AA]">Retour</Link>
        </header>

        {/* Mobile/Tablet Bottom Nav - scrollable */}
        <div className="lg:hidden bottom-nav fixed bottom-0 left-0 right-0 z-50 flex items-center h-16 px-1 safe-area-bottom overflow-x-auto">
          {sidebarItems.map(({ to, label, icon: Icon, exact, badge }) => {
            const active = exact ? location.pathname === to : location.pathname.startsWith(to) && to !== '/admin';
            return (
              <Link key={to} to={to} className={`flex flex-col items-center gap-0.5 px-2 sm:px-3 py-1 relative shrink-0 ${active ? 'text-[#D4AF37]' : 'text-[#A1A1AA]'}`}>
                <div className="relative">
                  <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                  {badge > 0 && <span className="absolute -top-1.5 -right-1.5 bg-[#EF4444] text-white text-[7px] font-bold w-3.5 h-3.5 rounded-full flex items-center justify-center">{badge}</span>}
                </div>
                <span className="text-[8px] sm:text-[9px] leading-tight whitespace-nowrap">{label}</span>
              </Link>
            );
          })}
        </div>

        {/* Main Content */}
        <main className="flex-1 p-3 sm:p-4 md:p-6 lg:p-8 pb-20 lg:pb-8 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
